import { useEffect, useRef, useState } from "react";
import {
  CalendarDays,
  ClipboardCheck,
  FileDown,
  ShieldCheck,
  TimerReset,
  UsersRound,
} from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { Loading, PageHeader, StatCard } from "../../components/shared";

const today = new Date().toISOString().slice(0, 10);
const thisMonth = today.slice(0, 7);

const emptyStaff = {
  name: "",
  staff_code: "",
  department_id: "",
  category_id: "",
  designation_id: "",
  engagement_type: "DIRECT",
  agency_provider_id: "",
  service_contract_id: "",
  joining_date: "",
  mobile: "",
  email: "",
  weekly_off: [],
  identity_number: "",
  private_address: "",
  emergency_contact: "",
  salary_rate: "",
  notes: "",
};

function Field({ children, label }) {
  return (
    <label className="grid gap-1.5 text-sm">
      <Label>{label}</Label>
      {children}
    </label>
  );
}

function TableEmpty({ columns, message, testid }) {
  return (
    <tr data-testid={testid}>
      <td
        className="p-8 text-center text-sm text-muted-foreground"
        colSpan={columns}
      >
        {message}
      </td>
    </tr>
  );
}

export default function SocietyStaff() {
  const [bootstrap, setBootstrap] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [staff, setStaff] = useState(null);
  const [shifts, setShifts] = useState(null);
  const [leave, setLeave] = useState(null);
  const [holidays, setHolidays] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [staffDialog, setStaffDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [filters, setFilters] = useState({ q: "", status: "ACTIVE" });

  const load = async () => {
    try {
      const query = new URLSearchParams(
        Object.entries(filters).filter(([, value]) => value),
      );
      const [
        boot,
        dash,
        staffRows,
        shiftRows,
        leaveRows,
        holidayRows,
        dayRows,
        monthRows,
      ] = await Promise.all([
        api.get("/society-staff/bootstrap"),
        api.get("/society-staff/dashboard"),
        api.get(`/society-staff/staff?${query.toString()}`),
        api.get("/society-staff/shifts"),
        api.get("/society-staff/leave"),
        api.get("/society-staff/holidays"),
        api.get(`/society-staff/attendance?work_date=${today}`),
        api.get(`/society-staff/reports/monthly-attendance?month=${thisMonth}`),
      ]);
      setBootstrap(boot.data);
      setDashboard(dash.data);
      setStaff(staffRows.data);
      setShifts(shiftRows.data);
      setLeave(leaveRows.data);
      setHolidays(holidayRows.data);
      setAttendance(dayRows.data);
      setMonthly(monthRows.data.rows);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load Workforce");
    }
  };

  useEffect(() => {
    load();
  }, [filters.q, filters.status]);

  if (
    !bootstrap ||
    !dashboard ||
    !staff ||
    !shifts ||
    !leave ||
    !holidays ||
    !attendance ||
    !monthly
  ) {
    return <Loading />;
  }

  return (
    <div data-testid="society-staff-page">
      <PageHeader
        title="Staff & Workforce"
        subtitle="Manage Society workforce records, duty coverage, attendance, and operational responsibilities."
      >
        <Button
          data-testid="staff-refresh-button"
          onClick={load}
          variant="outline"
        >
          Refresh
        </Button>
        <Button
          data-testid="staff-create-button"
          onClick={() => setStaffDialog(true)}
        >
          Add Staff
        </Button>
      </PageHeader>
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={UsersRound}
          label="Active staff"
          testid="staff-active-kpi"
          value={String(dashboard.active_staff)}
        />
        <StatCard
          icon={ClipboardCheck}
          label="Attendance marked today"
          testid="staff-attendance-kpi"
          tone="secondary"
          value={String(dashboard.today_marked)}
        />
        <StatCard
          icon={CalendarDays}
          label="Pending leave"
          testid="staff-leave-kpi"
          tone="amber"
          value={String(dashboard.pending_leave)}
        />
        <StatCard
          icon={TimerReset}
          label="Documents expiring"
          testid="staff-document-expiry-kpi"
          tone="rose"
          value={String(dashboard.expiring_documents.length)}
        />
      </section>
      {dashboard.sensitive_available &&
        dashboard.expiring_documents.length > 0 && (
          <section
            className="mt-5 border-l-4 border-amber-400 bg-amber-50 p-4"
            data-testid="staff-expiry-alerts"
          >
            <div className="font-display font-bold">
              Staff document expiry attention
            </div>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {dashboard.expiring_documents.map((document) => (
                <div
                  className="text-sm text-amber-950"
                  data-testid={`staff-expiry-${document.staff_document_id}`}
                  key={document.staff_document_id}
                >
                  {document.filename} · expires {document.expiry_date}
                </div>
              ))}
            </div>
          </section>
        )}
      <Tabs className="mt-7" defaultValue="staff">
        <TabsList
          className="h-auto flex-wrap"
          data-testid="staff-workspace-tabs"
        >
          <TabsTrigger data-testid="staff-tab-staff" value="staff">
            Staff Register
          </TabsTrigger>
          <TabsTrigger data-testid="staff-tab-attendance" value="attendance">
            Attendance
          </TabsTrigger>
          <TabsTrigger data-testid="staff-tab-shifts" value="shifts">
            Shifts & Roster
          </TabsTrigger>
          <TabsTrigger data-testid="staff-tab-leave" value="leave">
            Leave & Holidays
          </TabsTrigger>
          <TabsTrigger data-testid="staff-tab-reports" value="reports">
            Reports
          </TabsTrigger>
          <TabsTrigger data-testid="staff-tab-settings" value="settings">
            Configuration
          </TabsTrigger>
        </TabsList>
        <TabsContent value="staff">
          <StaffRegister
            filters={filters}
            onFilters={setFilters}
            onOpen={setSelectedStaff}
            staff={staff}
          />
        </TabsContent>
        <TabsContent value="attendance">
          <AttendancePanel
            attendance={attendance}
            onChanged={load}
            staff={staff}
          />
          <MonthlyAttendance rows={monthly} />
        </TabsContent>
        <TabsContent value="shifts">
          <ShiftRosterPanel onChanged={load} shifts={shifts} staff={staff} />
        </TabsContent>
        <TabsContent value="leave">
          <LeaveHolidayPanel
            bootstrap={bootstrap}
            holidays={holidays}
            leave={leave}
            onChanged={load}
            staff={staff}
          />
        </TabsContent>
        <TabsContent value="reports">
          <ReportsPanel />
        </TabsContent>
        <TabsContent value="settings">
          <MasterPanel bootstrap={bootstrap} onChanged={load} />
        </TabsContent>
      </Tabs>
      <StaffDialog
        bootstrap={bootstrap}
        onChanged={load}
        onOpenChange={setStaffDialog}
        open={staffDialog}
      />
      <Staff360
        assets={bootstrap.assets}
        onChanged={load}
        shifts={shifts}
        staffId={selectedStaff}
        onClose={() => setSelectedStaff("")}
      />
    </div>
  );
}

function StaffRegister({ filters, onFilters, onOpen, staff }) {
  return (
    <section>
      <div className="mb-5 flex flex-wrap gap-3">
        <Input
          className="max-w-sm"
          data-testid="staff-search-input"
          onChange={(event) => onFilters({ ...filters, q: event.target.value })}
          placeholder="Search staff, code, department"
          value={filters.q}
        />
        <select
          className="border bg-background px-3 py-2 text-sm"
          data-testid="staff-status-filter"
          onChange={(event) =>
            onFilters({ ...filters, status: event.target.value })
          }
          value={filters.status}
        >
          <option value="">All statuses</option>
          {["ACTIVE", "INACTIVE", "ON_LEAVE", "EXITED"].map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>
      <div
        className="overflow-x-auto border bg-card"
        data-testid="staff-register-table"
      >
        <table className="min-w-[980px] w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
            <tr>
              <th className="p-3">Staff</th>
              <th className="p-3">Department</th>
              <th className="p-3">Engagement</th>
              <th className="p-3">Shift</th>
              <th className="p-3">Status</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 ? (
              <TableEmpty
                columns={6}
                message="No staff records match these filters."
                testid="staff-register-empty"
              />
            ) : (
              staff.map((row) => (
                <tr
                  className="border-t"
                  data-testid={`staff-row-${row.staff_id}`}
                  key={row.staff_id}
                >
                  <td className="p-3">
                    <div className="font-medium">{row.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {row.staff_code || row.staff_id}
                    </div>
                  </td>
                  <td className="p-3">
                    {row.department_name}
                    <div className="text-xs text-muted-foreground">
                      {row.designation_name}
                    </div>
                  </td>
                  <td className="p-3">{row.engagement_type}</td>
                  <td className="p-3">{row.shift_name || "Not assigned"}</td>
                  <td className="p-3">{row.status}</td>
                  <td className="p-3 text-right">
                    <Button
                      data-testid={`staff-open-${row.staff_id}`}
                      onClick={() => onOpen(row.staff_id)}
                      size="sm"
                      variant="outline"
                    >
                      Open 360
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StaffDialog({ bootstrap, onChanged, onOpenChange, open }) {
  const [form, setForm] = useState(emptyStaff);
  const update = (key, value) => setForm({ ...form, [key]: value });
  const save = async () => {
    try {
      await api.post("/society-staff/staff", {
        ...form,
        idempotency_key: crypto.randomUUID(),
      });
      toast.success("Society staff record created");
      setForm(emptyStaff);
      onOpenChange(false);
      onChanged();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not create staff record",
      );
    }
  };
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className="max-h-[92vh] max-w-5xl overflow-y-auto"
        data-testid="staff-create-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">Add Society staff</DialogTitle>
          <DialogDescription>
            Create an operational staff record for this Society only.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Full name *">
            <Input
              data-testid="staff-name-input"
              onChange={(event) => update("name", event.target.value)}
              value={form.name}
            />
          </Field>
          <Field label="Staff code">
            <Input
              data-testid="staff-code-input"
              onChange={(event) => update("staff_code", event.target.value)}
              value={form.staff_code}
            />
          </Field>
          <Field label="Joining date">
            <Input
              data-testid="staff-joining-date-input"
              onChange={(event) => update("joining_date", event.target.value)}
              type="date"
              value={form.joining_date}
            />
          </Field>
          <Field label="Department *">
            <select
              data-testid="staff-department-select"
              onChange={(event) => update("department_id", event.target.value)}
              value={form.department_id}
            >
              <option value="">Select department</option>
              {bootstrap.departments
                .filter((item) => item.active)
                .map((item) => (
                  <option key={item.department_id} value={item.department_id}>
                    {item.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Category *">
            <select
              data-testid="staff-category-select"
              onChange={(event) => update("category_id", event.target.value)}
              value={form.category_id}
            >
              <option value="">Select category</option>
              {bootstrap.categories
                .filter((item) => item.active)
                .map((item) => (
                  <option key={item.category_id} value={item.category_id}>
                    {item.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Designation *">
            <select
              data-testid="staff-designation-select"
              onChange={(event) => update("designation_id", event.target.value)}
              value={form.designation_id}
            >
              <option value="">Select designation</option>
              {bootstrap.designations
                .filter((item) => item.active)
                .map((item) => (
                  <option key={item.designation_id} value={item.designation_id}>
                    {item.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Engagement">
            <select
              data-testid="staff-engagement-select"
              onChange={(event) =>
                update("engagement_type", event.target.value)
              }
              value={form.engagement_type}
            >
              {["DIRECT", "AGENCY", "CONTRACT"].map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </Field>
          {form.engagement_type === "AGENCY" && (
            <Field label="Agency provider">
              <select
                data-testid="staff-provider-select"
                onChange={(event) =>
                  update("agency_provider_id", event.target.value)
                }
                value={form.agency_provider_id}
              >
                <option value="">Select provider</option>
                {bootstrap.providers.map((item) => (
                  <option key={item.provider_id} value={item.provider_id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {form.engagement_type === "CONTRACT" && (
            <Field label="Service contract">
              <select
                data-testid="staff-contract-select"
                onChange={(event) =>
                  update("service_contract_id", event.target.value)
                }
                value={form.service_contract_id}
              >
                <option value="">Select contract</option>
                {bootstrap.contracts.map((item) => (
                  <option key={item.contract_id} value={item.contract_id}>
                    {item.contract_number} · {item.title}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Mobile">
            <Input
              data-testid="staff-mobile-input"
              onChange={(event) => update("mobile", event.target.value)}
              value={form.mobile}
            />
          </Field>
          <Field label="Email">
            <Input
              data-testid="staff-email-input"
              onChange={(event) => update("email", event.target.value)}
              value={form.email}
            />
          </Field>
          {bootstrap.sensitive_available && (
            <>
              <Field label="Identity number">
                <Input
                  data-testid="staff-identity-input"
                  onChange={(event) =>
                    update("identity_number", event.target.value)
                  }
                  value={form.identity_number}
                />
              </Field>
              <Field label="Emergency contact">
                <Input
                  data-testid="staff-emergency-input"
                  onChange={(event) =>
                    update("emergency_contact", event.target.value)
                  }
                  value={form.emergency_contact}
                />
              </Field>
              <Field label="Informational salary / rate">
                <Input
                  data-testid="staff-rate-input"
                  onChange={(event) =>
                    update("salary_rate", event.target.value)
                  }
                  value={form.salary_rate}
                />
              </Field>
              <div className="md:col-span-3">
                <Field label="Private address">
                  <Textarea
                    data-testid="staff-address-input"
                    onChange={(event) =>
                      update("private_address", event.target.value)
                    }
                    value={form.private_address}
                  />
                </Field>
              </div>
            </>
          )}
          <div className="md:col-span-3">
            <Field label="Operational notes">
              <Textarea
                data-testid="staff-notes-input"
                onChange={(event) => update("notes", event.target.value)}
                value={form.notes}
              />
            </Field>
          </div>
        </div>
        <Button data-testid="staff-save-button" onClick={save}>
          Create Staff Record
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function AttendancePanel({ attendance, onChanged, staff }) {
  const [form, setForm] = useState({
    staff_id: "",
    work_date: today,
    status: "PRESENT",
    check_in: "",
    check_out: "",
    remarks: "",
  });
  const save = async () => {
    try {
      await api.post("/society-staff/attendance", {
        ...form,
        idempotency_key: crypto.randomUUID(),
      });
      toast.success("Attendance marked");
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not mark attendance");
    }
  };
  const markAll = async () => {
    try {
      const marked = new Set(attendance.map((row) => row.staff_id));
      const rows = staff
        .filter((row) => !marked.has(row.staff_id))
        .map((row) => ({
          staff_id: row.staff_id,
          work_date: form.work_date,
          status: "PRESENT",
        }));
      if (!rows.length)
        return toast.message("All active staff are already marked for today");
      await api.post("/society-staff/attendance/bulk", {
        rows,
        idempotency_key: crypto.randomUUID(),
      });
      toast.success("Bulk attendance saved");
      onChanged();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not save bulk attendance",
      );
    }
  };
  const correct = async (row) => {
    const status = window.prompt("Correct status", row.status);
    const reason = window.prompt("Correction reason");
    if (!status || !reason) return;
    try {
      await api.patch(`/society-staff/attendance/${row.attendance_id}`, {
        status,
        reason,
      });
      toast.success("Attendance correction saved");
      onChanged();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not correct attendance",
      );
    }
  };
  return (
    <section className="grid gap-5 xl:grid-cols-[420px_1fr]">
      <div className="border bg-card p-5" data-testid="attendance-mark-form">
        <h2 className="font-display font-bold">Daily attendance</h2>
        <div className="mt-4 grid gap-3">
          <Field label="Staff">
            <select
              data-testid="attendance-staff-select"
              onChange={(event) =>
                setForm({ ...form, staff_id: event.target.value })
              }
              value={form.staff_id}
            >
              <option value="">Select staff</option>
              {staff.map((row) => (
                <option key={row.staff_id} value={row.staff_id}>
                  {row.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Work date">
            <Input
              data-testid="attendance-date-input"
              onChange={(event) =>
                setForm({ ...form, work_date: event.target.value })
              }
              type="date"
              value={form.work_date}
            />
          </Field>
          <Field label="Status">
            <select
              data-testid="attendance-status-select"
              onChange={(event) =>
                setForm({ ...form, status: event.target.value })
              }
              value={form.status}
            >
              {[
                "PRESENT",
                "ABSENT",
                "HALF_DAY",
                "LEAVE",
                "WEEKLY_OFF",
                "HOLIDAY",
              ].map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </Field>
          <Field label="Check-in">
            <Input
              data-testid="attendance-checkin-input"
              onChange={(event) =>
                setForm({ ...form, check_in: event.target.value })
              }
              type="time"
              value={form.check_in}
            />
          </Field>
          <Field label="Check-out">
            <Input
              data-testid="attendance-checkout-input"
              onChange={(event) =>
                setForm({ ...form, check_out: event.target.value })
              }
              type="time"
              value={form.check_out}
            />
          </Field>
          <Field label="Remarks">
            <Input
              data-testid="attendance-remarks-input"
              onChange={(event) =>
                setForm({ ...form, remarks: event.target.value })
              }
              value={form.remarks}
            />
          </Field>
          <Button data-testid="attendance-save-button" onClick={save}>
            Mark Attendance
          </Button>
          <Button
            data-testid="attendance-bulk-present-button"
            onClick={markAll}
            variant="outline"
          >
            Mark remaining active staff present
          </Button>
        </div>
      </div>
      <div
        className="overflow-x-auto border bg-card"
        data-testid="attendance-today-table"
      >
        <table className="min-w-[760px] w-full text-sm">
          <thead className="bg-muted/60 text-left">
            <tr>
              <th className="p-3">Staff</th>
              <th className="p-3">Status</th>
              <th className="p-3">Shift</th>
              <th className="p-3">Time</th>
              <th className="p-3">Hours</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {attendance.length === 0 ? (
              <TableEmpty
                columns={6}
                message="No attendance marked for today."
                testid="attendance-empty"
              />
            ) : (
              attendance.map((row) => (
                <tr
                  className="border-t"
                  data-testid={`attendance-row-${row.attendance_id}`}
                  key={row.attendance_id}
                >
                  <td className="p-3">{row.staff_name}</td>
                  <td className="p-3">{row.status}</td>
                  <td className="p-3">
                    {row.shift_name || "—"}
                    {row.overnight ? " · Overnight" : ""}
                  </td>
                  <td className="p-3">
                    {row.check_in || "—"} → {row.check_out || "—"}
                  </td>
                  <td className="p-3">
                    {Math.round((row.worked_minutes || 0) / 6) / 10}
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      data-testid={`attendance-correct-${row.attendance_id}`}
                      onClick={() => correct(row)}
                      size="sm"
                      variant="ghost"
                    >
                      Correct
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MonthlyAttendance({ rows }) {
  return (
    <section
      className="mt-6 overflow-x-auto border bg-card"
      data-testid="monthly-attendance-table"
    >
      <div className="border-b p-4 font-display font-bold">
        Monthly attendance · {thisMonth}
      </div>
      <table className="min-w-[700px] w-full text-sm">
        <thead className="bg-muted/60 text-left">
          <tr>
            <th className="p-3">Staff</th>
            <th className="p-3">Present days</th>
            <th className="p-3">Leave days</th>
            <th className="p-3">Absent days</th>
            <th className="p-3">Worked hours</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <TableEmpty
              columns={5}
              message="No monthly attendance data yet."
              testid="monthly-attendance-empty"
            />
          ) : (
            rows.map((row) => (
              <tr
                className="border-t"
                data-testid={`monthly-attendance-${row.staff_id}`}
                key={row.staff_id}
              >
                <td className="p-3">{row.staff_name}</td>
                <td className="p-3">{row.present}</td>
                <td className="p-3">{row.leave}</td>
                <td className="p-3">{row.absent}</td>
                <td className="p-3">
                  {Math.round((row.worked_minutes || 0) / 6) / 10}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}

function ShiftRosterPanel({ onChanged, shifts, staff }) {
  const [shift, setShift] = useState({
    name: "",
    start_time: "",
    end_time: "",
    break_minutes: 0,
    overnight: false,
  });
  const [roster, setRoster] = useState({
    work_date: today,
    shift_id: "",
    staff_ids: [],
  });
  const saveShift = async () => {
    try {
      await api.post("/society-staff/shifts", shift);
      toast.success("Shift saved");
      setShift({
        name: "",
        start_time: "",
        end_time: "",
        break_minutes: 0,
        overnight: false,
      });
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save shift");
    }
  };
  const saveRoster = async () => {
    try {
      await api.post("/society-staff/roster/bulk", {
        ...roster,
        idempotency_key: crypto.randomUUID(),
      });
      toast.success("Roster saved");
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save roster");
    }
  };
  return (
    <section className="grid gap-5 xl:grid-cols-2">
      <div className="border bg-card p-5" data-testid="staff-shift-form">
        <h2 className="font-display font-bold">Shift master</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field label="Shift name">
            <Input
              data-testid="shift-name-input"
              onChange={(event) =>
                setShift({ ...shift, name: event.target.value })
              }
              value={shift.name}
            />
          </Field>
          <Field label="Start">
            <Input
              data-testid="shift-start-input"
              onChange={(event) =>
                setShift({ ...shift, start_time: event.target.value })
              }
              type="time"
              value={shift.start_time}
            />
          </Field>
          <Field label="End">
            <Input
              data-testid="shift-end-input"
              onChange={(event) =>
                setShift({ ...shift, end_time: event.target.value })
              }
              type="time"
              value={shift.end_time}
            />
          </Field>
          <Field label="Break minutes">
            <Input
              data-testid="shift-break-input"
              onChange={(event) =>
                setShift({ ...shift, break_minutes: event.target.value })
              }
              type="number"
              value={shift.break_minutes}
            />
          </Field>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            checked={shift.overnight}
            data-testid="shift-overnight-checkbox"
            onChange={(event) =>
              setShift({ ...shift, overnight: event.target.checked })
            }
            type="checkbox"
          />
          Overnight shift
        </label>
        <Button
          className="mt-4"
          data-testid="shift-save-button"
          onClick={saveShift}
        >
          Save Shift
        </Button>
        <div className="mt-5 space-y-2">
          {shifts.map((row) => (
            <div
              className="border p-3 text-sm"
              data-testid={`shift-row-${row.shift_id}`}
              key={row.shift_id}
            >
              <b>{row.name}</b> · {row.start_time} – {row.end_time}
              {row.overnight ? " · Overnight" : ""}
            </div>
          ))}
        </div>
      </div>
      <div className="border bg-card p-5" data-testid="staff-roster-form">
        <h2 className="font-display font-bold">Simple daily roster</h2>
        <div className="mt-4 grid gap-3">
          <Field label="Roster date">
            <Input
              data-testid="roster-date-input"
              onChange={(event) =>
                setRoster({ ...roster, work_date: event.target.value })
              }
              type="date"
              value={roster.work_date}
            />
          </Field>
          <Field label="Shift">
            <select
              data-testid="roster-shift-select"
              onChange={(event) =>
                setRoster({ ...roster, shift_id: event.target.value })
              }
              value={roster.shift_id}
            >
              <option value="">Select shift</option>
              {shifts
                .filter((item) => item.active)
                .map((item) => (
                  <option key={item.shift_id} value={item.shift_id}>
                    {item.name}
                  </option>
                ))}
            </select>
          </Field>
          <div className="grid max-h-72 gap-2 overflow-y-auto border p-3">
            {staff.map((row) => (
              <label
                className="flex items-center gap-2 text-sm"
                key={row.staff_id}
              >
                <input
                  checked={roster.staff_ids.includes(row.staff_id)}
                  data-testid={`roster-staff-${row.staff_id}`}
                  onChange={(event) =>
                    setRoster({
                      ...roster,
                      staff_ids: event.target.checked
                        ? [...roster.staff_ids, row.staff_id]
                        : roster.staff_ids.filter((id) => id !== row.staff_id),
                    })
                  }
                  type="checkbox"
                />
                {row.name} · {row.shift_name || "No default shift"}
              </label>
            ))}
          </div>
          <Button data-testid="roster-save-button" onClick={saveRoster}>
            Save Roster
          </Button>
        </div>
      </div>
    </section>
  );
}

function LeaveHolidayPanel({ bootstrap, holidays, leave, onChanged, staff }) {
  const [leaveForm, setLeaveForm] = useState({
    staff_id: "",
    leave_type_id: "",
    start_date: today,
    end_date: today,
    reason: "",
  });
  const [holiday, setHoliday] = useState({ holiday_date: today, name: "" });
  const saveLeave = async () => {
    try {
      await api.post("/society-staff/leave", {
        ...leaveForm,
        idempotency_key: crypto.randomUUID(),
      });
      toast.success("Leave request saved");
      onChanged();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not save leave request",
      );
    }
  };
  const decide = async (row, status) => {
    try {
      await api.post(`/society-staff/leave/${row.leave_request_id}/decision`, {
        status,
      });
      toast.success(`Leave ${status.toLowerCase()}`);
      onChanged();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not update leave request",
      );
    }
  };
  const saveHoliday = async () => {
    try {
      await api.post("/society-staff/holidays", holiday);
      toast.success("Holiday saved");
      setHoliday({ holiday_date: today, name: "" });
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save holiday");
    }
  };
  return (
    <section className="space-y-6">
      <div className="grid gap-5 xl:grid-cols-2">
        <div className="border bg-card p-5" data-testid="staff-leave-form">
          <h2 className="font-display font-bold">Leave request</h2>
          <div className="mt-4 grid gap-3">
            <Field label="Staff">
              <select
                data-testid="leave-staff-select"
                onChange={(event) =>
                  setLeaveForm({ ...leaveForm, staff_id: event.target.value })
                }
                value={leaveForm.staff_id}
              >
                <option value="">Select staff</option>
                {staff.map((row) => (
                  <option key={row.staff_id} value={row.staff_id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Leave type">
              <select
                data-testid="leave-type-select"
                onChange={(event) =>
                  setLeaveForm({
                    ...leaveForm,
                    leave_type_id: event.target.value,
                  })
                }
                value={leaveForm.leave_type_id}
              >
                <option value="">Select leave type</option>
                {bootstrap.leave_types
                  .filter((item) => item.active)
                  .map((item) => (
                    <option key={item.leave_type_id} value={item.leave_type_id}>
                      {item.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Start">
              <Input
                data-testid="leave-start-input"
                onChange={(event) =>
                  setLeaveForm({ ...leaveForm, start_date: event.target.value })
                }
                type="date"
                value={leaveForm.start_date}
              />
            </Field>
            <Field label="End">
              <Input
                data-testid="leave-end-input"
                onChange={(event) =>
                  setLeaveForm({ ...leaveForm, end_date: event.target.value })
                }
                type="date"
                value={leaveForm.end_date}
              />
            </Field>
            <Field label="Reason">
              <Textarea
                data-testid="leave-reason-input"
                onChange={(event) =>
                  setLeaveForm({ ...leaveForm, reason: event.target.value })
                }
                value={leaveForm.reason}
              />
            </Field>
            <Button data-testid="leave-save-button" onClick={saveLeave}>
              Save Leave Request
            </Button>
          </div>
        </div>
        <div className="border bg-card p-5" data-testid="staff-holiday-form">
          <h2 className="font-display font-bold">Holiday calendar</h2>
          <div className="mt-4 grid gap-3">
            <Field label="Holiday date">
              <Input
                data-testid="holiday-date-input"
                onChange={(event) =>
                  setHoliday({ ...holiday, holiday_date: event.target.value })
                }
                type="date"
                value={holiday.holiday_date}
              />
            </Field>
            <Field label="Holiday name">
              <Input
                data-testid="holiday-name-input"
                onChange={(event) =>
                  setHoliday({ ...holiday, name: event.target.value })
                }
                value={holiday.name}
              />
            </Field>
            <Button data-testid="holiday-save-button" onClick={saveHoliday}>
              Add Holiday
            </Button>
          </div>
          <div className="mt-5 space-y-2">
            {holidays.length === 0 ? (
              <p
                className="text-sm text-muted-foreground"
                data-testid="holiday-empty"
              >
                No holidays configured.
              </p>
            ) : (
              holidays.map((row) => (
                <div
                  className="border p-3 text-sm"
                  data-testid={`holiday-row-${row.holiday_id}`}
                  key={row.holiday_id}
                >
                  {row.holiday_date} · {row.name}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <div
        className="overflow-x-auto border bg-card"
        data-testid="leave-register-table"
      >
        <table className="min-w-[800px] w-full text-sm">
          <thead className="bg-muted/60 text-left">
            <tr>
              <th className="p-3">Staff</th>
              <th className="p-3">Type</th>
              <th className="p-3">Period</th>
              <th className="p-3">Status</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {leave.length === 0 ? (
              <TableEmpty
                columns={5}
                message="No leave requests yet."
                testid="leave-empty"
              />
            ) : (
              leave.map((row) => (
                <tr
                  className="border-t"
                  data-testid={`leave-row-${row.leave_request_id}`}
                  key={row.leave_request_id}
                >
                  <td className="p-3">{row.staff_name}</td>
                  <td className="p-3">{row.leave_type_name}</td>
                  <td className="p-3">
                    {row.start_date} → {row.end_date}
                  </td>
                  <td className="p-3">{row.status}</td>
                  <td className="p-3 text-right">
                    {row.status === "PENDING" && (
                      <>
                        <Button
                          data-testid={`leave-approve-${row.leave_request_id}`}
                          onClick={() => decide(row, "APPROVED")}
                          size="sm"
                        >
                          Approve
                        </Button>
                        <Button
                          className="ml-2"
                          data-testid={`leave-reject-${row.leave_request_id}`}
                          onClick={() => decide(row, "REJECTED")}
                          size="sm"
                          variant="ghost"
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MasterPanel({ bootstrap, onChanged }) {
  const [kind, setKind] = useState("departments");
  const [name, setName] = useState("");
  const collections = {
    departments: bootstrap.departments,
    categories: bootstrap.categories,
    designations: bootstrap.designations,
    "leave-types": bootstrap.leave_types,
  };
  const save = async () => {
    try {
      await api.post(`/society-staff/masters/${kind}`, { name });
      toast.success("Workforce configuration saved");
      setName("");
      onChanged();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Could not save workforce configuration",
      );
    }
  };
  return (
    <section className="grid gap-5 lg:grid-cols-[380px_1fr]">
      <div className="border bg-card p-5" data-testid="staff-master-form">
        <h2 className="font-display font-bold">Workforce masters</h2>
        <div className="mt-4 grid gap-3">
          <Field label="Master">
            <select
              data-testid="staff-master-kind-select"
              onChange={(event) => setKind(event.target.value)}
              value={kind}
            >
              <option value="departments">Departments</option>
              <option value="categories">Categories</option>
              <option value="designations">Designations</option>
              <option value="leave-types">Leave types</option>
            </select>
          </Field>
          <Field label="Name">
            <Input
              data-testid="staff-master-name-input"
              onChange={(event) => setName(event.target.value)}
              value={name}
            />
          </Field>
          <Button data-testid="staff-master-save-button" onClick={save}>
            Add Configuration
          </Button>
        </div>
      </div>
      <div className="border bg-card p-5" data-testid="staff-master-list">
        <h2 className="font-display font-bold">
          Configured {kind.replace("-", " ")}
        </h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {collections[kind].map((item) => (
            <span
              className="border px-2 py-1 text-sm"
              data-testid={`staff-master-${kind}-${item.name}`}
              key={item.name}
            >
              {item.name}
              {item.active ? "" : " · inactive"}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReportsPanel() {
  const reports = [
    ["staff-register", "Staff Register"],
    ["monthly-attendance", "Monthly Attendance"],
    ["leave-register", "Leave Register"],
    ["document-expiry", "Document Expiry"],
  ];
  return (
    <section
      className="grid gap-4 md:grid-cols-2"
      data-testid="staff-reports-grid"
    >
      {reports.map(([slug, label]) => (
        <div
          className="border bg-card p-5"
          data-testid={`staff-report-${slug}`}
          key={slug}
        >
          <h2 className="font-display font-bold">{label}</h2>
          <div className="mt-4 flex gap-2">
            <a
              data-testid={`staff-report-view-${slug}`}
              href={`${API}/society-staff/reports/${slug}${slug === "monthly-attendance" ? `?month=${thisMonth}` : ""}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button size="sm" variant="outline">
                View
              </Button>
            </a>
            <a
              data-testid={`staff-report-csv-${slug}`}
              href={`${API}/society-staff/reports/${slug}?format=csv${slug === "monthly-attendance" ? `&month=${thisMonth}` : ""}`}
            >
              <Button size="sm" variant="outline">
                <FileDown className="mr-1 h-4 w-4" />
                CSV
              </Button>
            </a>
            <a
              data-testid={`staff-report-xlsx-${slug}`}
              href={`${API}/society-staff/reports/${slug}?format=xlsx${slug === "monthly-attendance" ? `&month=${thisMonth}` : ""}`}
            >
              <Button size="sm" variant="outline">
                XLSX
              </Button>
            </a>
          </div>
        </div>
      ))}
    </section>
  );
}

function Staff360({ assets, onChanged, shifts, staffId, onClose }) {
  const [data, setData] = useState(null);
  const [shift, setShift] = useState({ shift_id: "", effective_date: today });
  const [assetId, setAssetId] = useState("");
  const [documentType, setDocumentType] = useState("IDENTITY");
  const documentInput = useRef(null);
  const load = async () => {
    if (!staffId) return;
    try {
      const response = await api.get(`/society-staff/staff/${staffId}`);
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Staff record unavailable");
      onClose();
    }
  };
  useEffect(() => {
    setData(null);
    load();
  }, [staffId]);
  const refresh = () => {
    load();
    onChanged();
  };
  const assignShift = async () => {
    try {
      await api.post(`/society-staff/staff/${staffId}/shift`, shift);
      toast.success("Shift assigned");
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not assign shift");
    }
  };
  const assignAsset = async () => {
    try {
      await api.post(`/society-staff/staff/${staffId}/assets/${assetId}`);
      toast.success("Asset responsibility assigned");
      setAssetId("");
      refresh();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not assign asset responsibility",
      );
    }
  };
  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("document_type", documentType);
      await api.post(`/society-staff/staff/${staffId}/documents`, form);
      toast.success("Staff document uploaded");
      refresh();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not upload staff document",
      );
    } finally {
      event.target.value = "";
    }
  };
  if (!staffId) return null;
  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={Boolean(staffId)}>
      <DialogContent
        className="max-h-[94vh] max-w-6xl overflow-y-auto"
        data-testid="staff-360-dialog"
      >
        {!data ? (
          <Loading />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">
                {data.staff.name} · Staff 360
              </DialogTitle>
              <DialogDescription>
                Review workforce history, operational assignments, and protected
                records.
              </DialogDescription>
            </DialogHeader>
            <section className="grid gap-4 md:grid-cols-4">
              <div className="border p-3" data-testid="staff-360-department">
                <div className="text-xs text-muted-foreground">Department</div>
                <div className="font-medium">{data.staff.department_name}</div>
                <div className="text-xs">{data.staff.designation_name}</div>
              </div>
              <div className="border p-3" data-testid="staff-360-engagement">
                <div className="text-xs text-muted-foreground">Engagement</div>
                <div className="font-medium">{data.staff.engagement_type}</div>
                <div className="text-xs">
                  {data.staff.agency_provider_name ||
                    data.staff.service_contract_reference ||
                    "Direct Society staff"}
                </div>
              </div>
              <div className="border p-3" data-testid="staff-360-shift">
                <div className="text-xs text-muted-foreground">
                  Current shift
                </div>
                <div className="font-medium">
                  {data.staff.shift_name || "Not assigned"}
                </div>
              </div>
              <div className="border p-3" data-testid="staff-360-status">
                <div className="text-xs text-muted-foreground">Status</div>
                <div className="font-medium">{data.staff.status}</div>
              </div>
            </section>
            <section className="mt-5 grid gap-5 lg:grid-cols-2">
              <div className="border p-4" data-testid="staff-360-shift-panel">
                <h3 className="font-display font-bold">Shift assignment</h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <select
                    data-testid="staff-360-shift-select"
                    onChange={(event) =>
                      setShift({ ...shift, shift_id: event.target.value })
                    }
                    value={shift.shift_id}
                  >
                    <option value="">Select shift</option>
                    {shifts
                      .filter((item) => item.active)
                      .map((item) => (
                        <option key={item.shift_id} value={item.shift_id}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                  <Input
                    data-testid="staff-360-shift-date"
                    onChange={(event) =>
                      setShift({ ...shift, effective_date: event.target.value })
                    }
                    type="date"
                    value={shift.effective_date}
                  />
                </div>
                <Button
                  className="mt-3"
                  data-testid="staff-360-shift-save"
                  onClick={assignShift}
                  variant="outline"
                >
                  Assign Shift
                </Button>
              </div>
              <div className="border p-4" data-testid="staff-360-assets-panel">
                <h3 className="font-display font-bold">Asset responsibility</h3>
                <div className="mt-3 flex gap-2">
                  <select
                    data-testid="staff-360-asset-select"
                    onChange={(event) => setAssetId(event.target.value)}
                    value={assetId}
                  >
                    <option value="">Select asset</option>
                    {assets.map((item) => (
                      <option key={item.asset_id} value={item.asset_id}>
                        {item.asset_code} · {item.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    data-testid="staff-360-asset-assign"
                    onClick={assignAsset}
                    variant="outline"
                  >
                    Assign
                  </Button>
                </div>
                <div className="mt-3 space-y-2">
                  {data.responsible_assets.map((item) => (
                    <div
                      className="border p-2 text-sm"
                      data-testid={`staff-360-asset-${item.asset_id}`}
                      key={item.asset_id}
                    >
                      {item.asset_code} · {item.name}
                    </div>
                  ))}
                </div>
              </div>
            </section>
            {data.staff.sensitive_available ? (
              <section
                className="mt-5 border p-4"
                data-testid="staff-360-documents"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="font-display font-bold">
                    Protected documents
                  </h3>
                  <div className="flex gap-2">
                    <select
                      data-testid="staff-document-type-select"
                      onChange={(event) => setDocumentType(event.target.value)}
                      value={documentType}
                    >
                      {[
                        "IDENTITY",
                        "ADDRESS_PROOF",
                        "POLICE_VERIFICATION",
                        "MEDICAL",
                        "TRAINING",
                        "LICENSE",
                        "AGREEMENT",
                        "PHOTO",
                        "OTHER",
                      ].map((item) => (
                        <option key={item}>{item.replaceAll("_", " ")}</option>
                      ))}
                    </select>
                    <input
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      data-testid={`staff-document-input-${staffId}`}
                      hidden
                      onChange={upload}
                      ref={documentInput}
                      type="file"
                    />
                    <Button
                      data-testid={`staff-document-upload-${staffId}`}
                      onClick={() => documentInput.current?.click()}
                      size="sm"
                      variant="outline"
                    >
                      Upload
                    </Button>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {data.documents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No protected staff documents.
                    </p>
                  ) : (
                    data.documents.map((item) => (
                      <a
                        className="block text-sm text-primary underline"
                        data-testid={`staff-document-${item.staff_document_id}`}
                        href={`${API}/society-staff/documents/${item.staff_document_id}/download`}
                        key={item.staff_document_id}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.document_type.replaceAll("_", " ")} ·{" "}
                        {item.filename}
                        {item.expiry_date
                          ? ` · expires ${item.expiry_date}`
                          : ""}
                      </a>
                    ))
                  )}
                </div>
              </section>
            ) : (
              <section
                className="mt-5 border-l-4 border-slate-300 bg-slate-50 p-4 text-sm"
                data-testid="staff-sensitive-restricted"
              >
                Protected personal details and documents require the explicit
                sensitive-data permission.
              </section>
            )}
            <section className="mt-5 grid gap-5 lg:grid-cols-2">
              <div className="border p-4" data-testid="staff-360-helpdesk">
                <h3 className="font-display font-bold">
                  Assigned Helpdesk Tickets
                </h3>
                {data.helpdesk_tickets.length === 0 ? (
                  <p className="mt-3 text-sm text-muted-foreground">
                    No Helpdesk tickets assigned.
                  </p>
                ) : (
                  data.helpdesk_tickets.map((item) => (
                    <div
                      className="mt-2 border p-2 text-sm"
                      data-testid={`staff-ticket-${item.ticket_id}`}
                      key={item.ticket_id}
                    >
                      {item.ticket_number} · {item.subject}
                      <div className="text-xs text-muted-foreground">
                        {item.status}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="border p-4" data-testid="staff-360-attendance">
                <h3 className="font-display font-bold">Recent attendance</h3>
                {data.attendance.length === 0 ? (
                  <p className="mt-3 text-sm text-muted-foreground">
                    No attendance history yet.
                  </p>
                ) : (
                  data.attendance.slice(0, 8).map((item) => (
                    <div
                      className="mt-2 border-l-2 border-primary/30 pl-3 text-sm"
                      data-testid={`staff-360-attendance-${item.attendance_id}`}
                      key={item.attendance_id}
                    >
                      <b>{item.work_date}</b> · {item.status}
                      {item.overnight ? " · Overnight" : ""}
                      {item.correction_history?.length
                        ? ` · ${item.correction_history.length} correction(s)`
                        : ""}
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
